import { datadogRum } from "@datadog/browser-rum";

interface AuthErrorLogOptions {
  error: unknown;
  errorType: string;
  hookName: string;
  context?: Record<string, any>;
}

const ERROR_SOURCE = "auth";

/**
 * Logs authentication errors to DataDog with consistent formatting
 * and flexible context. All auth errors will have source: "auth"
 *
 * @param options - Error logging options
 * @param options.error - The error object or message
 * @param options.errorType - Type of error (e.g., 'email_verification', 'sso_authentication')
 * @param options.hookName - Name of the hook where error occurred
 * @param options.context - Additional context data (not strictly typed)
 */
export function logAuthError({
  error,
  errorType,
  hookName,
  context = {},
}: AuthErrorLogOptions): void {
  // Extract error message
  const errorMessage =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : JSON.stringify(error);

  // Log to console for development
  console.error(`[Auth Error - ${errorType}]:`, errorMessage, {
    hookName,
    ...context,
  });

  // Log to DataDog if available
  if (typeof window !== "undefined" && datadogRum) {
    const errorObject =
      error instanceof Error ? error : new Error(errorMessage);

    datadogRum.addError(errorObject, {
      source: ERROR_SOURCE,
      context: {
        errorType,
        hookName,
        originalError: errorMessage,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        ...context,
      },
    });
  }
}
